<template>
  <div class="tenant-selector" ref="selectorRef">
    <!-- Selected Tenant Display -->
    <button 
      class="selector-trigger" 
      @click="toggleDropdown"
      :class="{ active: isOpen }">
      <div class="trigger-content">
        <div class="trigger-icon">
          <i class="fas fa-building"></i>
        </div>
        <div class="trigger-text">
          <span class="trigger-label">Selected Tenant</span>
          <span class="trigger-value">{{ selectedTenantName }}</span>
        </div>
      </div>
      <i class="fas fa-chevron-down trigger-arrow" :class="{ rotated: isOpen }"></i>
    </button>

    <!-- Dropdown Menu -->
    <transition name="dropdown">
      <div v-if="isOpen" class="selector-dropdown">
        <!-- Search Input -->
        <div class="dropdown-search">
          <i class="fas fa-search search-icon"></i>
          <input 
            ref="searchInput"
            type="text" 
            v-model="searchQuery" 
            placeholder="Search tenant by name or domain..."
            class="search-input"
            @keydown.esc="closeDropdown"
          >
          <button 
            v-if="searchQuery" 
            @click="clearSearch"
            class="search-clear">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading || searchLoading" class="dropdown-loading">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <span class="loading-text">{{ searchLoading ? 'Searching tenants...' : 'Loading tenants...' }}</span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="dropdown-error">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ error }}</span>
          <button @click="loadTenants" class="btn-retry-small">Retry</button>
        </div>

        <!-- Tenant List -->
        <div v-else-if="tenants.length > 0" class="dropdown-list">
          <button
            v-for="tenant in tenants"
            :key="tenant.id"
            @click="selectTenant(tenant)"
            class="tenant-item"
            :class="{ selected: isSelected(tenant.id) }">
            <div class="selection-indicator">
              <div class="radio-outer">
                <div class="radio-inner" v-if="isSelected(tenant.id)"></div>
              </div>
            </div>
            <div class="tenant-info">
              <span class="tenant-name">{{ tenant.name }}</span>
              <div class="tenant-meta">
                <span class="tenant-code">{{ tenant.code }}</span>
                <span class="meta-dot"></span>
                <span class="tenant-domain">{{ tenant.domain }}</span>
              </div>
            </div>
          </button>
        </div>

        <!-- Empty State -->
        <div v-else class="dropdown-empty">
          <i class="fas fa-search"></i>
          <span>No tenants found matching "{{ searchQuery }}"</span>
        </div>

        <!-- Footer -->
        <div class="dropdown-footer">
          <span class="footer-text">{{ tenants.length }} of {{ totalTenants }} tenants</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'TenantSelector',

  setup() {
    const store = useStore()
    
    const isOpen = ref(false)
    const searchQuery = ref('')
    const selectorRef = ref(null)
    const searchInput = ref(null)
    const searchLoading = ref(false)
    const searchDebounceTimer = ref(null)
    const serverTotal = ref(0)

    // Computed
    const tenants = computed(() => store.state.extranet.tenants || [])
    const selectedTenant = computed(() => store.state.extranet.selectedTenant)
    const loading = computed(() => store.state.extranet.loading)
    const error = computed(() => store.state.extranet.error)

    const selectedTenantName = computed(() => {
      return selectedTenant.value?.name || 'Select Tenant'
    })

    const totalTenants = computed(() => serverTotal.value || tenants.value.length)

    // Methods
    function toggleDropdown() {
      isOpen.value = !isOpen.value
      
      if (isOpen.value) {
        // Focus search input when dropdown opens
        nextTick(() => {
          searchInput.value?.focus()
        })
        
        // Load tenants if not loaded
        if (tenants.value.length === 0) {
          loadTenants()
        }
      }
    }

    function closeDropdown() {
      isOpen.value = false
      if (searchQuery.value) {
        searchQuery.value = ''
        // Reload full list without search filter
        loadTenants()
      }
    }

    function clearSearch() {
      searchQuery.value = ''
      loadTenants()
    }

    async function loadTenants(search = '') {
      try {
        await store.dispatch('extranet/fetchTenants', { search })
      } catch (error) {
        console.error('Failed to load tenants:', error)
      }
    }

    function selectTenant(tenant) {
      store.dispatch('extranet/selectTenant', tenant)
      closeDropdown()
      
      // Reload current page to reflect tenant change
      window.location.reload()
    }

    function isSelected(tenantId) {
      return selectedTenant.value?.id === tenantId
    }

    // Click outside handler
    function handleClickOutside(event) {
      if (selectorRef.value && !selectorRef.value.contains(event.target)) {
        closeDropdown()
      }
    }

    // Lifecycle
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      
      // Load tenants on mount
      if (tenants.value.length === 0) {
        loadTenants()
      }
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
      }
    })

    // Watch for Escape key
    watch(isOpen, (newValue) => {
      if (newValue) {
        document.addEventListener('keydown', handleEscapeKey)
      } else {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    })

    // Watch searchQuery for server-side search with debounce
    watch(searchQuery, (newValue) => {
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value)
      }
      // Skip if dropdown is closed
      if (!isOpen.value) return

      searchDebounceTimer.value = setTimeout(async () => {
        searchLoading.value = true
        try {
          await loadTenants(newValue)
        } finally {
          searchLoading.value = false
        }
      }, 300)
    })



    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        closeDropdown()
      }
    }

    return {
      isOpen,
      searchQuery,
      selectorRef,
      searchInput,
      searchLoading,
      tenants,
      selectedTenant,
      loading,
      error,
      selectedTenantName,
      totalTenants,
      toggleDropdown,
      closeDropdown,
      clearSearch,
      loadTenants,
      selectTenant,
      isSelected
    }
  }
}
</script>

<style scoped>
/* Tenant Selector Container */
.tenant-selector {
  position: relative;
  z-index: 100;
}

/* Trigger Button */
.selector-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: var(--extranet-rounded);
  cursor: pointer;
  transition: all 0.2s;
  width: 320px;
}

.selector-trigger:hover {
  border-color: #cbd5e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.selector-trigger.active {
  border-color: #e2e8f0;
  border-bottom-color: transparent;
  border-radius: var(--extranet-rounded) var(--extranet-rounded) 0 0;
  background-color: #ffffff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.trigger-icon {
  width: 36px;
  height: 36px;
  background: var(--extranet-primary);
  border-radius: var(--extranet-rounded);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.trigger-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
}

.trigger-label {
  font-size: 11px;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.trigger-value {
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.trigger-arrow {
  font-size: 12px;
  color: #a0aec0;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.trigger-arrow.rotated {
  transform: rotate(180deg);
}

/* Dropdown */
.selector-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  max-height: 500px;
  background: white;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 var(--extranet-rounded) var(--extranet-rounded);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Dropdown Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Search */
.dropdown-search {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f7fafc;
}

.search-icon {
  color: #a0aec0;
  font-size: 14px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #2d3748;
  outline: none;
}

.search-input::placeholder {
  color: #a0aec0;
}

.search-clear {
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.search-clear:hover {
  background: #e2e8f0;
  color: #4a5568;
}

/* Loading State */
.dropdown-loading {
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.loading-text {
  font-size: 13px;
  color: #718096;
}

/* Error State */
.dropdown-error {
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #dc3545;
  font-size: 13px;
}

.btn-retry-small {
  margin-top: 8px;
  padding: 6px 16px;
  background: var(--extranet-primary);
  color: white;
  border: none;
  border-radius: var(--extranet-rounded);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-retry-small:hover {
  background: #a0171b;
}

/* Tenant List */
.dropdown-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.tenant-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: var(--extranet-rounded);
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  text-align: left;
  margin-bottom: 2px;
}

.tenant-item:hover {
  background: #f8fafc;
}

.tenant-item.selected {
  background: #fffafa;
}

.selection-indicator {
  flex-shrink: 0;
}

.radio-outer {
  width: 18px;
  height: 18px;
  border: 2px solid #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.tenant-item.selected .radio-outer {
  border-color: var(--extranet-primary);
}

.radio-inner {
  width: 8px;
  height: 8px;
  background: var(--extranet-primary);
  border-radius: 50%;
  animation: scale-up 0.2s ease-out;
}

@keyframes scale-up {
  from { transform: scale(0); }
  to { transform: scale(1); }
}

.tenant-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tenant-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tenant-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
}

.meta-dot {
  width: 3px;
  height: 3px;
  background: #cbd5e1;
  border-radius: 50%;
}

.tenant-code {
  font-weight: 500;
}

/* Empty State */
.dropdown-empty {
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #a0aec0;
  font-size: 13px;
  text-align: center;
}

.dropdown-empty i {
  font-size: 32px;
  color: #cbd5e0;
}

/* Footer */
.dropdown-footer {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #f7fafc;
}

.footer-text {
  font-size: 12px;
  color: #718096;
}

/* Scrollbar */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f7fafc;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Responsive */
@media (max-width: 768px) {
  .selector-trigger {
    min-width: 200px;
  }

  .selector-dropdown {
    width: 320px;
  }
}
</style>
